import { ContactInfo, ExtendedContact } from "types/customer"

export const parsePrimaryDefaultExtraIndex = (key: string | undefined): number | null => {
  if (key == null || key === "" || key === "main") return null
  const m = /^extra:(\d+)$/.exec(key)
  return m ? Number(m[1]) : null
}

export const deriveDefaultPrimaryKeysFromContactInfos = (
  list: ContactInfo[]
): { email: string; phone: string } => {
  let email = "main"
  let phone = "main"
  list.forEach((row, i) => {
    if (row.contact_type === "email" && row.is_primary) {
      email = `extra:${i}`
    }
    if (row.contact_type === "phone" && row.is_primary) {
      phone = `extra:${i}`
    }
  })
  return { email, phone }
}

export const applyPrimaryKeysToContactInfo = (
  list: ContactInfo[] | null | undefined,
  defaultPrimaryEmailKey: string,
  defaultPrimaryPhoneKey: string
): ContactInfo[] => {
  if (!list?.length) return []
  return list.map((row, i) => ({
    ...row,
    is_primary:
      row.contact_type === "email"
        ? defaultPrimaryEmailKey === `extra:${i}`
        : defaultPrimaryPhoneKey === `extra:${i}`,
  }))
}

export const extendedToContactInfosForPrimary = (
  list: ExtendedContact[] | null | undefined
): ContactInfo[] => {
  if (!list?.length) return []
  return list
    .filter(
      (e) =>
        !e.fk_business_customer_contact_id &&
        (e.contact_type === "email" || e.contact_type === "phone")
    )
    .map((e) => ({
      id: e.id,
      contact_type: e.contact_type as "email" | "phone",
      contact_value: String(e.contact_value ?? ""),
      contact_description: e.contact_description ?? "",
      is_billing: !!e.is_billing,
      is_notification: !!e.is_notification,
      is_primary: !!e.is_primary,
    }))
}

export const extendedToContactInfosForAdditional = (
  list: ExtendedContact[] | null | undefined,
  contactId: number
): ContactInfo[] => {
  if (!list?.length) return []
  return list
    .filter(
      (e) =>
        e.fk_business_customer_contact_id === contactId &&
        (e.contact_type === "email" || e.contact_type === "phone")
    )
    .map((e) => ({
      id: e.id,
      contact_type: e.contact_type as "email" | "phone",
      contact_value: String(e.contact_value ?? ""),
      contact_description: e.contact_description ?? "",
      is_billing: !!e.is_billing,
      is_notification: !!e.is_notification,
    }))
}

type BillingEmailCustomer = {
  email?: string | null
  is_billing?: boolean
  billing_emails?: string[] | null
  contacts?: Array<{ email?: string | null; is_billing?: boolean } | null> | null
  extended_contacts?: ExtendedContact[] | null
}

/** All emails marked billing across primary, extras, and additional contacts */
export const collectCustomerBillingEmails = (
  customer?: BillingEmailCustomer | null
): string[] => {
  if (!customer) return []

  const dedupe = (list: string[]) => {
    const seen = new Set<string>()
    const out: string[] = []
    for (const raw of list) {
      const email = raw.trim()
      if (!email) continue
      const key = email.toLowerCase()
      if (seen.has(key)) continue
      seen.add(key)
      out.push(email)
    }
    return out
  }

  const fromApi = (customer.billing_emails || []).filter(
    (e): e is string => typeof e === "string" && e.trim().length > 0
  )
  if (fromApi.length > 0) return dedupe(fromApi)

  const emails: string[] = []
  if (customer.is_billing && customer.email?.trim()) {
    emails.push(customer.email.trim())
  }
  for (const row of customer.extended_contacts || []) {
    if (
      row.contact_type === "email" &&
      row.is_billing &&
      row.contact_value?.trim()
    ) {
      emails.push(row.contact_value.trim())
    }
  }
  for (const contact of customer.contacts || []) {
    if (!contact) continue
    if (contact.is_billing && contact.email?.trim()) {
      emails.push(contact.email.trim())
    }
  }
  return dedupe(emails)
}

export const normalizeContactInfoForApi = (
  list?: ContactInfo[] | null
): Omit<ContactInfo, "id">[] => {
  if (!list?.length) return []
  return list
    .filter((x) => (x.contact_value || "").toString().trim())
    .map((x) => ({
      contact_type: x.contact_type,
      contact_value: (x.contact_value || "").toString().trim(),
      contact_description: (x.contact_description || "")
        .toString()
        .trim()
        .slice(0, 255),
      is_billing: x.contact_type === "email" ? !!x.is_billing : false,
      is_notification: !!x.is_notification,
      is_primary: !!x.is_primary,
    }))
}

export const emailsMatch = (a: string, b: string) => {
  const na = (a || "").trim().toLowerCase()
  const nb = (b || "").trim().toLowerCase()
  return na.length > 0 && na === nb
}

export const phonesMatch = (a: string, b: string) => {
  const na = (a || "").replace(/\D/g, "")
  const nb = (b || "").replace(/\D/g, "")
  return na.length > 0 && na === nb
}

export const filterPrimaryCustomerExtras = (
  extended: ExtendedContact[] | null | undefined
): ExtendedContact[] =>
  (extended || []).filter(
    (e) =>
      !e.fk_business_customer_contact_id &&
      (e.contact_type === "email" || e.contact_type === "phone")
  )

export type PrimaryCustomerHeaderContacts = {
  displayEmail: string
  displayPhone: string
  hasFavoriteEmail: boolean
  hasFavoritePhone: boolean
}

/** Per-type: favorited extra when set, otherwise business_customer column */
export const getPrimaryCustomerHeaderContacts = (
  extended: ExtendedContact[] | null | undefined,
  bcEmail?: string | null,
  bcPhone?: string | null
): PrimaryCustomerHeaderContacts => {
  const extras = filterPrimaryCustomerExtras(extended)
  const favoriteEmail = extras.find(
    (e) =>
      e.contact_type === "email" &&
      e.is_primary &&
      String(e.contact_value || "").trim()
  )
  const favoritePhone = extras.find(
    (e) =>
      e.contact_type === "phone" &&
      e.is_primary &&
      String(e.contact_value || "").trim()
  )
  return {
    displayEmail: favoriteEmail
      ? String(favoriteEmail.contact_value).trim()
      : (bcEmail || "").trim(),
    displayPhone: favoritePhone
      ? String(favoritePhone.contact_value).trim()
      : (bcPhone || "").trim(),
    hasFavoriteEmail: !!favoriteEmail,
    hasFavoritePhone: !!favoritePhone,
  }
}

const syntheticMainRow = (
  type: "email" | "phone",
  value: string,
  isBilling: boolean = false,
  isNotification: boolean = false
): ContactInfo => ({
  contact_type: type,
  contact_value: value,
  contact_description: "",
  is_billing: type === "email" ? isBilling : false,
  is_notification: isNotification,
  is_primary: false,
})

/**
 * Popup list only: excludes header values and is_primary rows; injects bc main when favorited.
 */
export const buildPrimaryCustomerDropdownEntries = (
  extended: ExtendedContact[] | null | undefined,
  bcEmail?: string | null,
  bcPhone?: string | null,
  bcIsBilling?: boolean,
  bcIsNotification?: boolean
): ContactInfo[] => {
  const extras = filterPrimaryCustomerExtras(extended)
  const header = getPrimaryCustomerHeaderContacts(extended, bcEmail, bcPhone)
  const mainEmailTrim = (bcEmail || "").trim()
  const mainPhoneTrim = (bcPhone || "").trim()

  const filtered = extras.filter((e) => {
    const v = String(e.contact_value || "").trim()
    if (!v) return false
    if (e.is_primary) return false
    if (
      e.contact_type === "email" &&
      header.displayEmail &&
      emailsMatch(v, header.displayEmail)
    ) {
      return false
    }
    if (
      e.contact_type === "phone" &&
      header.displayPhone &&
      phonesMatch(v, header.displayPhone)
    ) {
      return false
    }
    return true
  })

  const rows: ContactInfo[] = filtered.map((e) => ({
    id: e.id,
    contact_type: e.contact_type as "email" | "phone",
    contact_value: String(e.contact_value ?? "").trim(),
    contact_description: e.contact_description ?? "",
    is_billing: !!e.is_billing,
    is_notification: !!e.is_notification,
    is_primary: false,
  }))

  if (
    header.hasFavoriteEmail &&
    mainEmailTrim &&
    !rows.some(
      (e) => e.contact_type === "email" && emailsMatch(e.contact_value, mainEmailTrim)
    )
  ) {
    rows.unshift(syntheticMainRow("email", mainEmailTrim, !!bcIsBilling, !!bcIsNotification))
  }
  if (
    header.hasFavoritePhone &&
    mainPhoneTrim &&
    !rows.some(
      (e) => e.contact_type === "phone" && phonesMatch(e.contact_value, mainPhoneTrim)
    )
  ) {
    rows.unshift(syntheticMainRow("phone", mainPhoneTrim, false, !!bcIsNotification))
  }

  return rows
}

export const buildAdditionalContactDropdownEntries = (
  extras: ExtendedContact[],
  contactEmail?: string | null,
  contactPhone?: string | null
): ContactInfo[] => {
  const headerEmail = (contactEmail || "").trim()
  const headerPhone = (contactPhone || "").trim()

  return extras
    .filter((e) => {
      const v = String(e.contact_value || "").trim()
      if (!v) return false
      if (e.is_primary) return false
      if (e.contact_type === "email" && headerEmail && emailsMatch(v, headerEmail)) {
        return false
      }
      if (e.contact_type === "phone" && headerPhone && phonesMatch(v, headerPhone)) {
        return false
      }
      return true
    })
    .map((e) => ({
      id: e.id,
      contact_type: e.contact_type as "email" | "phone",
      contact_value: String(e.contact_value ?? "").trim(),
      contact_description: e.contact_description ?? "",
      is_billing: !!e.is_billing,
      is_notification: !!e.is_notification,
      is_primary: false,
    }))
}
